import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft
} from 'lucide-react';
import { useState } from 'react';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

import { offeringDetailsSchema } from '../../_validator';

const OfferingDetailsForm = ({
  initialValues,
  onHandleSubmit,
  onPreviousStep
}) => {
  const form = useForm({
    resolver: zodResolver(offeringDetailsSchema),
    defaultValues: { ...initialValues }
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = (values) => {
    onHandleSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="length"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Duration in minutes</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) =>
                      field.onChange(Number.parseInt(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground px-0"
          >
            {isExpanded ? 'Hide Optional Fields' : 'Show Optional Fields'}
            {isExpanded ? (
              <ChevronUp className="ml-1 h-6 w-6 shrink-0 opacity-70" />
            ) : (
              <ChevronDown className="ml-1 h-6 w-6 shrink-0 opacity-70" />
            )}
          </Button>
          {isExpanded && (
            <>
              <FormField
                control={form.control}
                name="requirements"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Requirements (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter requirements..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      Minimum level of skill, fitness, experience, etc. required
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="includedItems"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Included (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter included items..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      Enter a list of items or things that are included in this
                      offering.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notIncludedItems"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Not Included (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter items not included..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      Enter a list of items or things that are not included in
                      this offering.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
        <div className="flex justify-between pt-8">
          <Button
            type="button"
            className="gap-1"
            onClick={onPreviousStep}
            variant="outline"
          >
            <ChevronLeft />
            Previous
          </Button>
          <Button type="submit" className="gap-1">
            Next
            <ChevronRight />
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OfferingDetailsForm;
